import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  AccountCircle,
  Email,
  Security,
  DateRange,
  AdminPanelSettings,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel as CancelIcon,
  Download,
  Policy,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { changePassword, logoutUser } from '../../store/slices/authSlice';
import { getDecodedToken } from '../../utils/auth';
import EmailVerificationPrompt from '../../components/auth/EmailVerificationPrompt';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const authError = useSelector((state: RootState) => state.auth.error);
  const decodedToken = getDecodedToken();
  
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportingData, setExportingData] = useState(false);

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordSuccess(false);

    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    if (!user?.id) {
      setPasswordError('User ID not available');
      return;
    }

    try {
      setLoading(true);
      await dispatch(changePassword({
        userId: user.id,
        oldPassword,
        newPassword,
      })).unwrap();
      
      setPasswordSuccess(true);
      setTimeout(async () => {
        setChangePasswordOpen(false);
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordSuccess(false);
        // Logout the user after password change
        await dispatch(logoutUser());
        // Redirect to homepage
        navigate('/');
      }, 2000);
    } catch (error) {
      setPasswordError('Failed to change password. Please check your current password.');
    } finally {
      setLoading(false);
    }
  };

  const handleClosePasswordDialog = () => {
    setChangePasswordOpen(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess(false);
  };

  const handleExportData = async () => {
    try {
      setExportingData(true);
      const response = await api.get('/my-data');
      
      // Create a blob from the JSON data
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      // Create download link
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `my-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Show success message
      alert('Your data has been exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setExportingData(false);
    }
  };

  if (!user) {
    return (
      <Container>
        <Alert severity="error">User information not available</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        {t('profile.title')}
      </Typography>

      {/* Show email verification prompt if needed */}
      {user && !user.is_active && (
        <EmailVerificationPrompt />
      )}

      {/* User Information Card */}
      <Card 
        sx={{ 
          mb: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AccountCircle sx={{ fontSize: 64, color: '#3388ff', mr: 2 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {user.username}
              </Typography>
              {user.is_platform_admin && (
                <Chip
                  icon={<AdminPanelSettings />}
                  label={t('profile.platformAdmin')}
                  color="secondary"
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
            {/* User ID */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                User ID
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                {user.id}
              </Typography>
            </Box>

            {/* Email */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('profile.email')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email sx={{ fontSize: 20, color: '#666' }} />
                <Typography variant="body1">
                  {user.email || 'Not available'}
                </Typography>
              </Box>
            </Box>

            {/* Role */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('profile.role')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Security sx={{ fontSize: 20, color: '#666' }} />
                <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                  {user.is_platform_admin 
                    ? t('profile.platformAdmin') 
                    : user.is_pilot === true && user.user_type === 'club_manager'
                      ? t('profile.dualRole', 'Pilot & Club Manager')
                    : user.is_pilot === false || user.user_type === 'club_manager'
                      ? t('profile.clubManager', 'Club Manager')
                      : t('profile.pilot', 'Pilot')}
                </Typography>
              </Box>
            </Box>

            {/* Account Status */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('profile.accountInfo')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {user.is_active ? (
                  <>
                    <CheckCircle sx={{ fontSize: 20, color: '#4caf50' }} />
                    <Typography variant="body1" sx={{ color: '#4caf50' }}>
                      {t('users.status.active')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <CancelIcon sx={{ fontSize: 20, color: '#f44336' }} />
                    <Typography variant="body1" sx={{ color: '#f44336' }}>
                      {t('users.status.inactive')}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* Created Date */}
            {user.created_at && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {t('profile.memberSince')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DateRange sx={{ fontSize: 20, color: '#666' }} />
                  <Typography variant="body1">
                    {format(new Date(user.created_at), 'MMMM d, yyyy')}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Last Updated */}
            {user.updated_at && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {t('profile.lastUpdated')}
                </Typography>
                <Typography variant="body1">
                  {format(new Date(user.updated_at), 'MMMM d, yyyy HH:mm')}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Actions Card */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('profile.accountInfo')}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<Security />}
              onClick={() => setChangePasswordOpen(true)}
              sx={{
                background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)',
                boxShadow: '0 3px 5px 2px rgba(51, 136, 255, .3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0066ff 30%, #3388ff 90%)',
                },
              }}
            >
              {t('profile.changePassword')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportData}
              disabled={exportingData}
              sx={{
                borderColor: '#3388ff',
                color: '#3388ff',
                '&:hover': {
                  borderColor: '#0066ff',
                  backgroundColor: 'rgba(51, 136, 255, 0.04)',
                },
              }}
            >
              {exportingData ? (
                <CircularProgress size={20} />
              ) : (
                t('profile.exportData')
              )}
            </Button>
            <Button
              variant="text"
              startIcon={<Policy />}
              onClick={() => window.open('/service-addendum', '_blank')}
              sx={{
                color: '#666',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              {t('profile.dataProtection')}
            </Button>
          </Box>

          {authError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {authError}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Associated Clubs (if role is club) */}
      {decodedToken?.role === 'club' && decodedToken.clubs && decodedToken.clubs.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('users.table.clubs')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {decodedToken.clubs.map((clubId) => (
                <Chip
                  key={clubId}
                  label={clubId}
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onClose={handleClosePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('profile.changePassword')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {passwordError}
              </Alert>
            )}
            {passwordSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Password changed successfully! You will be logged out in a moment...
              </Alert>
            )}
            
            <TextField
              fullWidth
              label={t('profile.currentPassword')}
              type={showOldPassword ? 'text' : 'password'}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      edge="end"
                    >
                      {showOldPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              label={t('profile.newPassword')}
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              helperText="Minimum 8 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              label={t('profile.confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog} disabled={loading}>
            {t('users.cancel')}
          </Button>
          <Button
            onClick={handlePasswordChange}
            variant="contained"
            disabled={loading || passwordSuccess}
            sx={{
              background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #0066ff 30%, #3388ff 90%)',
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : t('profile.updatePassword')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfilePage;